import * as React from "react";

import { cn } from "@/lib/utils";

export type InputProps = React.ComponentProps<"input"> & {
  /** `dense` is the in-table size the line editors use. Numbers sit right, words left (KP, Q37). */
  density?: "default" | "dense";
  /**
   * A number field: the phone shows its number keys, and a value of 0 shows as a faint placeholder,
   * so a click and a number replace it without deleting the 0 first (review Q25). Once anything is
   * typed the field shows exactly what was typed, so "0.5" can still be entered.
   */
  numeric?: boolean;
};

const isZero = (value: unknown) =>
  value !== undefined && value !== null && String(value).trim() !== "" && Number(value) === 0;

/** The mockup's .in: a 1px line, the page background, no shadow. */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, density = "default", numeric = false, value, placeholder, inputMode, onFocus, onBlur, onChange, ...props }, ref) => {
    const [focused, setFocused] = React.useState(false);
    const [typed, setTyped] = React.useState(false);
    const hideZero = numeric && isZero(value) && !(focused && typed);
    return (
      <input
        type={type}
        inputMode={numeric ? "decimal" : inputMode}
        className={cn(
          "w-full rounded-md border border-input bg-bg text-ink transition-colors duration-150 placeholder:text-mute focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60",
          density === "dense" ? "px-1.5 py-1 text-sm" : "px-2.5 py-2 text-base",
          density === "dense" && (numeric || type === "number") && "text-right",
          className,
        )}
        ref={ref}
        value={hideZero ? "" : value}
        placeholder={hideZero ? "0" : placeholder}
        onFocus={(event) => {
          setFocused(true);
          setTyped(false);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          setTyped(false);
          onBlur?.(event);
        }}
        onChange={(event) => {
          setTyped(true);
          onChange?.(event);
        }}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
